"use client";

import HardNavLink from "@/components/shared/HardNavLink";
import { useState, useRef, useEffect } from "react";
import {
  Star, FileText, BookOpen, Target, PlusCircle,
  MessageSquare, Heart, Bookmark, Share2, Check,
} from "lucide-react";
import type { ReviewWithUser } from "@/actions/reviews";

// ── Helper: relative time ─────────────────────────────────────
function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(s => (
        <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(rating) ? "text-amber-400 fill-amber-400" : "text-[#e2e8f0]"}`} />
      ))}
    </div>
  );
}

function relativeTime(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)   return "Vừa xong";
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  const days = Math.floor(diff / 86400);
  if (days === 1)  return "Hôm qua";
  if (days < 7)   return `${days} ngày trước`;
  return new Date(dateStr).toLocaleDateString("vi-VN");
}

// ── Helper: extract first img src + strip html ────────────────
function parseContent(html: string): { imgSrc: string | null; text: string } {
  const imgMatch = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  const imgSrc   = imgMatch ? imgMatch[1] : null;
  const text     = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return { imgSrc, text };
}

// ── Review card ───────────────────────────────────────────────
export default function ReviewCard({ review }: { review: ReviewWithUser }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

function handleShare(e: React.MouseEvent) {
  e.preventDefault();
  navigator.clipboard.writeText(`${window.location.origin}/reviews/${review.slug || review.id}`);
  setCopied(true);
  if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
}

useEffect(() => () => {
  if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
}, []);
  const name = (review as any).user?.full_name ?? (review as any).user?.username ?? "Người dùng";
  const time = relativeTime(review.created_at);

  const TARGET_ICON: Record<string, React.ReactNode> = {
    exam:   <FileText className="w-3 h-3" />,
    path:   <BookOpen className="w-3 h-3" />,
    system: <Star className="w-3 h-3" />,
  };
  const TARGET_LABEL: Record<string, string> = {
    exam: "Đề thi", path: "Lộ trình", system: "Hệ thống",
  };

  const attachedCount = review.attached_questions?.length ?? 0;
  const { imgSrc, text } = parseContent(review.content ?? "");

  // Count matched vs new questions
  const matchedCount = (review.attached_questions ?? []).filter((q: any) => q.type === "matched" || !q.type).length;
  const newCount     = (review.attached_questions ?? []).filter((q: any) => q.type === "new").length;

  return (
    <HardNavLink href={`/reviews/${review.slug || review.id}`} className="block bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden hover:border-teal-300 hover:shadow-md transition-all">

      {/* ── HEADER ── */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          {review.user?.avatar_url ? (
            <img src={review.user.avatar_url} alt={name}
              className="w-9 h-9 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
              <span className="text-teal-700 text-[13px] font-bold">{name.slice(0,1).toUpperCase()}</span>
            </div>
          )}
          <div>
            <p className="text-[13px] font-semibold text-[#0f172a]">{name}</p>
            <p className="text-[10px] text-[#94a3b8]">{time}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Stars rating={review.stars} />
          {review.target_type && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#f1f5f9] text-[#64748b] border border-[#e2e8f0]">
              {TARGET_ICON[review.target_type]}
              {TARGET_LABEL[review.target_type] ?? review.target_type}
            </span>
          )}
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div className="px-5 pb-3">
        {/* Title */}
        {review.title && (
          <p className="text-[14px] font-bold text-[#0f172a] mb-2 leading-snug">{review.title}</p>
        )}

        {/* Body: có ảnh → 2 cột, không ảnh → full width */}
        {imgSrc ? (
          <div className="flex gap-3">
            <div className="w-1/3 shrink-0">
              <img src={imgSrc} alt="" className="w-full aspect-video object-cover rounded-xl" />
            </div>
            <p className="flex-1 text-[13px] text-[#334155] leading-relaxed line-clamp-3">{text}</p>
          </div>
        ) : (
          <p className="text-[13px] text-[#334155] leading-relaxed line-clamp-3">{text}</p>
        )}

        {/* Tags */}
        {review.tags && review.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {review.tags.slice(0, 5).map(tag => (
              <span key={tag}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── FOOTER ── */}
      <div className="px-5 py-3 border-t border-[#f1f5f9] flex items-center gap-1 flex-wrap">

        {/* Metadata badges */}
        {(review as any).org && (
          <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[9px] font-bold bg-blue-50 text-blue-600 border border-blue-200 mr-1">
            {(review as any).org}
          </span>
        )}
        {(review as any).position && (
          <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[9px] font-bold bg-violet-50 text-violet-600 border border-violet-200 mr-2">
            {(review as any).position}
          </span>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 text-[11px] text-[#94a3b8] flex-1">
          {matchedCount > 0 && (
            <span className="flex items-center gap-1">
              <Target className="w-3 h-3" />{matchedCount} trùng
            </span>
          )}
          {newCount > 0 && (
            <span className="flex items-center gap-1">
              <PlusCircle className="w-3 h-3" />{newCount} mới
            </span>
          )}
          <span className="flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />{(review as any).comment_count ?? 0}
          </span>
          <span className="flex items-center gap-1">
            <Heart className="w-3 h-3" />{(review as any).like_count ?? 0}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={e => { e.preventDefault(); setSaved(p => !p); }}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
              saved ? "text-teal-600 bg-teal-50" : "text-[#94a3b8] hover:bg-[#f1f5f9]"
            }`}
            title="Lưu"
          >
            <Bookmark className={`w-3.5 h-3.5 ${saved ? "fill-teal-600" : ""}`} />
          </button>
          <div className="relative group">
  <button
    onClick={handleShare}
    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
      copied ? "text-green-500 bg-green-50" : "text-[#94a3b8] hover:bg-[#f1f5f9]"
    }`}
    title="Chia sẻ"
  >
    {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
  </button>
  {copied && (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-lg bg-gray-900 text-white text-[10px] font-medium whitespace-nowrap pointer-events-none">
      Đã sao chép!
    </div>
  )}
</div>
        </div>
      </div>
    </HardNavLink>
  );
}
